import * as React from "react";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineOppositeContent from "@mui/lab/TimelineOppositeContent";
// import moment from "moment";
import moment from "moment-timezone"; 
import { Link } from "react-router-dom";
import { ENTERTAINMENT_LOG_CATEGORIES, LOG_ACTIONS } from "../../utils/Constant/LogConstant";

const ENTERTAINMENT_RVC_LIST_PATH = "/shore/back-office/entertainment/revenue-center";

// Opt-in palette reusing the app's established status colors (same hex values
// as statusChipStyle in MobileAncillary/Entertainment reports), so new
// modules get clearly distinct dot colors without altering old modules'
// existing LOG_ACTION -> color mapping below.
const EXTENDED_ACTION_COLORS = [
  [LOG_ACTIONS.ACTIVATED, "#0ab402"],   // green
  [LOG_ACTIONS.CREATED, "#0ab402"],     // green
  [LOG_ACTIONS.UPDATED, "#1565c0"],     // blue
  [LOG_ACTIONS.ASSIGNED, "#1565c0"],    // blue
  [LOG_ACTIONS.SYNCED, "#f5a623"],      // orange
  [LOG_ACTIONS.DUPLICATED, "#f5a623"],  // orange
  [LOG_ACTIONS.DEACTIVATED, "#616161"], // grey
  [LOG_ACTIONS.REMOVED, "#eb6363"],     // red
  [LOG_ACTIONS.CLAIMED, "#1565c0"],     // blue
  [LOG_ACTIONS.VOIDED, "#616161"],      // grey
  [LOG_ACTIONS.REQUESTED, "#f5a623"],   // orange
  [LOG_ACTIONS.DOWNLOADED, "#0ab402"],  // green
];
const EXTENDED_DEFAULT_COLOR = "#9e9e9e"; // light grey

const LogItem = ({ item, slug, location, hidetitle, removedClickable, extendedColors }) => {

  let data = JSON.parse(item.data);

  // Removed is only clickable when the caller confirms its delete is a soft
  // delete (removedClickable) - otherwise the record no longer exists to
  // navigate to.
  const nonClickableActions = removedClickable ? [] : [LOG_ACTIONS.REMOVED];

  // Simplified check for non-clickable actions
  const isNonClickable = Array.isArray(item.actions)
    ? item.actions.some(action => nonClickableActions.some(op => String(action).includes(op)))
    : nonClickableActions.some(op => String(item.actions).includes(op));

  const [dotColor, setDotColor] = React.useState("red");

  React.useEffect(() => {
    const actions = item.actions;
    if (!(actions && typeof actions.includes === "function")) return;

    if (extendedColors) {
      const match = EXTENDED_ACTION_COLORS.find(([action]) => actions.includes(action));
      setDotColor(match?.[1] || EXTENDED_DEFAULT_COLOR);
      return;
    }

    if (actions.includes(LOG_ACTIONS.CREATED)) {
      setDotColor("blue");
    } else if (actions.includes(LOG_ACTIONS.ACTIVATED)) {
      setDotColor("#a88332");
    } else if (actions.includes(LOG_ACTIONS.DEACTIVATED)) {
      setDotColor("black");
    } else if (actions.includes(LOG_ACTIONS.UPDATED)) {
      setDotColor("green");
    } else if (actions.includes(LOG_ACTIONS.DUPLICATED)) {
      setDotColor("#9c27b0");
    } else if (
      actions.includes(LOG_ACTIONS.ASSIGNED) ||
      actions.includes(LOG_ACTIONS.REMOVED)
    ) {
      setDotColor("#ff9800");
    }
  }, [item, extendedColors]);
  return (
    <TimelineItem>
      <TimelineOppositeContent color="text.secondary">
        {/* {moment(item?.created_at).local().format("MMMM DD,YYYY hh:mm a")} */}
        {moment(item?.created_at)
          .tz("Asia/Kuala_Lumpur")
          .format("MMMM DD,YYYY hh:mm a")}
      </TimelineOppositeContent>
      <TimelineSeparator>
        <TimelineDot style={{ background: dotColor }} />
        <TimelineConnector />
      </TimelineSeparator>
      <TimelineContent sx={{ display: "flex", flexDirection: "column", gap: "5px", wordBreak: "break-word" }}>
        {data?.category && (
          <span
            style={{
              alignSelf: "flex-start",
              background: "#eceff1",
              color: "#455a64",
              fontSize: "11px",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.3px",
              padding: "2px 8px",
              borderRadius: "10px",
            }}
          >
            {data.category}
          </span>
        )}
        {(!hidetitle && data?.title) && (
          <div
            style={{ color: "blue", wordBreak: "break-word" }}
            dangerouslySetInnerHTML={{ __html: data?.title }}
          />
        )}
        {(() => {
          // Itemizer/Menu Item live under a nested route that needs the
          // parent RVC's id too - the flat slug+id pattern can't express
          // that, so build their two-segment link separately.
          let link = null;
          if (data?.category === ENTERTAINMENT_LOG_CATEGORIES.ITEMIZER && data?.rvcId && data?.id) {
            link = `${ENTERTAINMENT_RVC_LIST_PATH}/${data.rvcId}/itemizer/edit/${data.id}`;
          } else if (data?.category === ENTERTAINMENT_LOG_CATEGORIES.MENU_ITEM && data?.rvcId && data?.id) {
            link = `${ENTERTAINMENT_RVC_LIST_PATH}/${data.rvcId}/menu-item/edit/${data.id}`;
          } else if (slug && data?.id) {
            link = slug + data.id;
          }

          return link && !isNonClickable ? (
            <Link to={link} style={{ color: dotColor, wordBreak: "break-word" }}>
              {item.actions}
            </Link>
          ) : (
            <div style={{ color: dotColor, wordBreak: "break-word" }}>{item.actions}</div>
          );
        })()}
        {item.user && (
          <div>
            {item?.user?.firstName + " " + item?.user?.lastName}
          </div>
        )}
        {(data?.excNo || data?.packageId) && (
          <div style={{ display: "flex", gap: "10px", color: "#757575", fontSize: "13px" }}>
            {data?.excNo && <span>Code: {data.excNo}</span>}
            {data?.packageId && <span>Package ID: {data.packageId}</span>}
          </div>
        )}
        {(data?.transactionId || data?.cabinNumber || data?.guestName || data?.amount != null) && (
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", color: "#757575", fontSize: "13px" }}>
            {data?.transactionId && <span>Transaction ID: {data.transactionId}</span>}
            {data?.cabinNumber && <span>Cabin: {data.cabinNumber}</span>}
            {data?.guestName && <span>Guest: {data.guestName}</span>}
            {data?.amount != null && <span>Amount: {data.amount}</span>}
          </div>
        )}
        {data?.fileName && (
          <div style={{ color: "#757575", fontSize: "13px" }}>
            File: {data.fileName}
          </div>
        )}
        {data?.reason && (
          <div style={{ color: "#757575", fontSize: "13px" }}>
            Reason: {data.reason}
          </div>
        )}
        {data?.location_id && (
          <div style={{ display: "flex", gap: "5px" }}>
            <div>for </div>
            <div style={{ color: "green" }}>
              {
                location.filter((item) => item.id === data.location_id)?.[0]
                  ?.location_name
              }
            </div>
          </div>
        )}
      </TimelineContent>
    </TimelineItem>
  );
};

export default LogItem;
